import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {proteinNestedComponentColumnCustomEval} from 'antibodies/nested-data-creation/full-screen/proteinNestedComponentColumnCustomEval';
import {generateSpreadsheetComponentColumns} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  AntibodyComponentAndRelatedSchemas,
  NdcSpreadsheetComponentFormatType,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  EvalTableView,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableKind';
import {
  makeSchemaFieldStructuredColumn,
  makeStructuredTableColumn,
} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CustomEvalFnAndType, CustomEvalType} from 'notebook/tables/constants/CustomEvalConstants';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {CellIndex, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {RegistrationStatusFilterValue} from 'search/filters/RegistrationStatusFilterForm';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';

const REGISTERED_ONLY_FILTER: Filter = {
  key: FilterKey.REGISTRATION_STATUS,
  operator: Operator.IS,
  value: RegistrationStatusFilterValue.REGISTERED,
};

const NDC_SUPPORTED_SCHEMA_FIELD_TYPES = new Set<FieldType>([
  FieldType.TEXT,
  FieldType.LONG_TEXT,
  FieldType.INTEGER,
  FieldType.FLOAT,
  FieldType.BOOLEAN,
  FieldType.BIOENTITY_LINK,
]);

export interface ProteinCreationTableMetadata {
  antibodyFormat: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
  componentAndRelatedSchemas: AntibodyComponentAndRelatedSchemas;
  aaSchemaIdByComponentLabel: Map<string, string>;
}

function getSchemaFilterForComponent(
  name: string,
  componentAndRelatedSchemas: AntibodyComponentAndRelatedSchemas,
  aaSchemaIdByComponentLabel: Map<string, string>,
  componentType: NdcSpreadsheetComponentFormatType
): Array<Filter> {
  if (componentType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS) {
    return [
      {
        key: FilterKey.SCHEMA,
        operator: Operator.IS_ONE_OF,
        value: [
          ...componentAndRelatedSchemas.aaPairSchemaIds,
          ...componentAndRelatedSchemas.dnaPairSchemaIds,
        ],
      },
      REGISTERED_ONLY_FILTER,
    ];
  }
  const aaSchemaId = aaSchemaIdByComponentLabel.get(name);
  let dnaSchemaIds: Set<string> = new Set<string>();
  if (componentType === NdcSpreadsheetComponentFormatType.DOMAINS) {
    dnaSchemaIds = componentAndRelatedSchemas.dnaDomainSchemaIds;
  } else if (componentType === NdcSpreadsheetComponentFormatType.CHAINS) {
    dnaSchemaIds = componentAndRelatedSchemas.dnaChainSchemaIds;
  }
  const schemaIds = [...(aaSchemaId ? [aaSchemaId] : []), ...dnaSchemaIds];
  return [{key: FilterKey.SCHEMA, operator: Operator.IS_ONE_OF, value: schemaIds}, REGISTERED_ONLY_FILTER];
}

export type ProteinCreationStructuredTable = StructuredTable<ProteinCreationTableMetadata, RowMetadata>;

export default class ProteinCreationStructuredTableAdapter extends GenericStructuredTableAdapter<
  ProteinCreationTableMetadata,
  RowMetadata
> {
  static getAdapter(): ProteinCreationStructuredTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROTEIN_CREATION
    ] as ProteinCreationStructuredTableAdapter;
  }

  override getActionFunctions(): ActionFunctions<ProteinCreationTableMetadata, RowMetadata> {
    return {secondary: [], overflow: []};
  }

  getBlankRowMetadata(): RowMetadata {
    return {};
  }

  getDisplayName(structuredTable: ProteinCreationStructuredTable): string {
    return structuredTable.name;
  }

  override _getAllSupportedColumns(params: {
    entryId: string | null;
    metadata: ProteinCreationTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const {antibodyFormat, startingType} = params.metadata;
    const domainRequiredByLabel = new Map(
      antibodyFormat.domains.map((domain) => [domain.label, !domain.optional])
    );
    const {componentAndRelatedSchemas, aaSchemaIdByComponentLabel} = params.metadata;

    const componentColumns = generateSpreadsheetComponentColumns(antibodyFormat, startingType).map(
      ({label, componentType}) => ({
        ...makeStructuredTableColumn({
          columnType: TableColumnType.PROTEIN_NESTED_COMPONENT,
          name: label,
          isRequired: domainRequiredByLabel.get(label) ?? true,
        }),
        columnType: TableColumnType.PROTEIN_NESTED_COMPONENT,
        bioentityTypeFilters:
          componentType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS
            ? [ModelTypeConstants.BASIC_FOLDER_ITEM]
            : [ModelTypeConstants.SEQUENCE, ModelTypeConstants.PROTEIN],
        additionalFilters: getSchemaFilterForComponent(
          label,
          componentAndRelatedSchemas,
          aaSchemaIdByComponentLabel,
          componentType
        ),
        allowsResidues: componentType !== NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS,
      })
    );

    const schemaFieldColumns = params.schemas.flatMap((schema) =>
      schema.allFields
        .filter(
          (field) =>
            field.archiveRecord == null &&
            !field.isComputed &&
            NDC_SUPPORTED_SCHEMA_FIELD_TYPES.has(field.fieldType)
        )
        .map((field) =>
          makeSchemaFieldStructuredColumn(field, params.selectorsById, schema.columnGroup, schema.allFields)
        )
    );

    return [...componentColumns, ...schemaFieldColumns];
  }

  override toTable(structuredTable: ProteinCreationStructuredTable): Table {
    return {...super.toTable(structuredTable), allowColumnEdits: false, defaultAffixedColumnCount: 0};
  }

  override getTableSpecificCustomEvalFn(
    tableView: EvalTableView<ProteinCreationTableMetadata, RowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{tableMetadata: ProteinCreationTableMetadata; rowMetadata: RowMetadata}> | null {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    if (column.columnType !== TableColumnType.PROTEIN_NESTED_COMPONENT) {
      return null;
    }
    return {customEvalFn: proteinNestedComponentColumnCustomEval, customEvalType: CustomEvalType.VALIDATE};
  }

  override allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: false,
    };
  }

  override isColumnUserConfigurable(
    structuredTable: ProteinCreationStructuredTable,
    column: StructuredTableColumn | null
  ): boolean {
    return false;
  }
}
